import { InfoCircleOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Flex,
  Form,
  Row,
  Space,
  Switch,
  Tabs,
  Tooltip,
  Typography,
  type UploadFile
} from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type ValidateErrorEntity } from "rc-field-form/es/interface"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  findFirstErrorTabKey,
  getTabErrorCounts
} from "../../../../components/Forms/TabbedEditForm/tab-field-utils.ts"
import { type TabDef } from "../../../../components/Forms/TabbedEditForm/tab-types.ts"
import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker"
import InputNumber from "../../../../components/Input/InputNumber"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput"
import SelectComponent from "../../../../components/Select"
import { placeholders } from "../../../../constants/placeholders"
import { LOCAL_STATES } from "../../../../constants/states"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone"
import { ComplianceDocumentUploadModal } from "../ComplianceDocumentUploadModal.tsx"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  TRACKED_COMPLIANCE_DOCUMENT_TYPES
} from "../../constants/compliance.ts"
import type {
  SubcontractorComplianceDocument,
  SubcontractorComplianceDocumentType
} from "../../types/types.ts"
import { fetchSubcontractorsFilterOptions } from "../../redux/subcontractors.thunks"
import { DocumentUpload } from "./DocumentUpload.tsx"
import { type SubcontractorFormData } from "./subcontractor-form-data.ts"
import {
  type SubcontractorFormValues,
  toSubcontractorFormData
} from "./subcontractor-form-submit.ts"

const { Text, Title } = Typography

const ENTITY_TYPE_OPTIONS = [
  { label: "LLC", value: "llc" },
  { label: "Corp", value: "corp" },
  { label: "S-Corp", value: "s_corp" },
  { label: "Sole Prop", value: "sole_prop" },
  { label: "Partnership", value: "partnership" }
]

const EIN_PATTERN = /^\d{2}-?\d{7}$/
const SSN_PATTERN = /^\d{3}-?\d{2}-?\d{4}$/

const CSLB_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Expired", value: "expired" },
  { label: "Inactive", value: "inactive" }
]
// The CSLB's rule is numeric, up to 8 digits — real licenses run 6-7 digits.
const CSLB_LICENSE_NUMBER_PATTERN = /^\d{1,8}$/
const CSLB_MINIMUM_BOND_AMOUNT = 25000

// A plain `type`/`min`/`transform` rule runs `transform` even on an untouched
// (undefined) field, turning it into 0/NaN and tripping `min` on a field nobody
// filled in — a custom validator short-circuits first. Shared by the contractor's
// bond and the qualifying individual's, which have the same $25,000 minimum.
const validateBondAmount = (_rule: unknown, value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return Promise.resolve()
  }
  if (Number(value) < CSLB_MINIMUM_BOND_AMOUNT) {
    return Promise.reject(
      new Error(
        `Minimum bond amount is $${CSLB_MINIMUM_BOND_AMOUNT.toLocaleString()}`
      )
    )
  }
  return Promise.resolve()
}

const PAYMENT_METHOD_OPTIONS = [
  { label: "ACH", value: "ach" },
  { label: "Wire", value: "wire" },
  { label: "Check", value: "check" }
]
const ACH_ROUTING_NUMBER_PATTERN = /^\d{9}$/
const ACH_ACCOUNT_NUMBER_PATTERN = /^\d{4,17}$/


// Which fields sit under which section — read by the tab error badge and by
// the jump to the section holding the first invalid field on a failed save.
const OVERVIEW_FIELDS = [
  "legal_business_name", "first_name", "middle_name", "last_name", "email",
  "phone", "price_per_hour", "location", "specialization", "send_invitation",
  "primary_contact_name", "primary_contact_email", "primary_contact_phone"
]

// No `*_document` / `*_expiration_date` keys: a compliance document is not a
// field of this form. It is uploaded through `upload_compliance_document`,
// which is the only write path the API has for one.
const DOCUMENT_FIELDS = [
  "cslb_license_number",
  "cslb_classification", "cslb_status", "cslb_issue_date", "cslb_bond_amount",
  "cslb_bond_number", "cslb_qualifier_bond_amount", "cslb_qualifier_bond_number"
]

const TAX_PAYMENT_FIELDS = [
  "entity_type", "business_address", "business_city", "business_state",
  "business_zip_code", "ein", "ssn", "w9_document", "w9_validity_date",
  "payment_method", "ach_routing_number", "ach_account_number"
]

const normFile = (e: { fileList?: UploadFile[] } | UploadFile[]) =>
  Array.isArray(e) ? e : e?.fileList

interface SubcontractorFormProps {
  loading?: boolean
  initialValues?: Partial<SubcontractorFormData> & {
    id?: string
    ein_last4?: string | null
    ssn_last4?: string | null
    cslb_expiration_date?: string | null
    ach_routing_number_last4?: string | null
    ach_account_number_last4?: string | null
    compliance_documents?: SubcontractorComplianceDocument[]
  }
  /** The name a caller creating inline (e.g. from a finance row) already typed. */
  defaultName?: string
  innerForm?: FormInstance
  onSave: (data: SubcontractorFormData) => void
  onCancel: () => void
}

export const SubcontractorForm = ({
  loading,
  initialValues,
  defaultName,
  innerForm,
  onSave,
  onCancel
}: SubcontractorFormProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [form] = Form.useForm<SubcontractorFormValues>(innerForm)

  const fetching = useAppSelector((state) => state.subcontractors.saveFetching)
  const filterOptions = useAppSelector(
    (state) => state.subcontractors.filterOptions
  )

  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )

  const inviteSubcontractors = permissions?.features?.subcontractor_invite
  const createSubcontractors = permissions?.subcontractors?.subcontractor_create

  const defaultSendInvitation = useMemo(() => {
    if (inviteSubcontractors && !createSubcontractors) return true
    return !(!inviteSubcontractors && createSubcontractors)
  }, [createSubcontractors, inviteSubcontractors])

  const showToggle =
    !initialValues && inviteSubcontractors && createSubcontractors

  const sendInvitation = Form.useWatch("send_invitation", form)
  const entityType = Form.useWatch("entity_type", form)
  const paymentMethod = Form.useWatch("payment_method", form)

  // Creation asks only for the basics, whether the subcontractor is added
  // directly or invited — the invitation only decides whether an email goes
  // out. Everything else, compliance documents included, is entered against a
  // record that already exists, which is what made them reachable again.
  const [activeSection, setActiveSection] = useState("overview")
  // Which compliance document the upload modal is open for, if any. A
  // subcontractor that already exists never uploads one through this form's own
  // fields: `SubcontractorSerializer` — what the viewset uses for
  // `update`/`partial_update` — declares none of the six per-type keys, so DRF
  // drops them and answers 200 with nothing stored. `upload_compliance_document`
  // is the one write path, and the modal is what calls it.
  const [managingType, setManagingType] =
    useState<SubcontractorComplianceDocumentType | null>(null)
  // This form owns no refetch of its own (the parent's `initialValues` prop
  // is what's authoritative), so a successful upload is reflected here only
  // — an immediate answer to "did that work?" without waiting on the parent
  // to re-read the record, which still happens the normal way once this
  // modal (and, usually, the whole edit form) closes.
  // `undefined` means "nothing uploaded in this session, trust initialValues";
  // `null` means "just uploaded a document that genuinely has no expiry". Both
  // used to be `null`, so uploading a dateless document fell through the `??`
  // below and kept showing the previous document's date — a date that no
  // longer belonged to anything on file.
  const [licenseExpirationOverride, setLicenseExpirationOverride] =
    useState<string | null | undefined>(undefined)
  // Same idea, per document type, for the rows below: what was just uploaded in
  // this session, before the parent re-reads the record.
  const [uploadedExpirations, setUploadedExpirations] = useState<
    Partial<Record<SubcontractorComplianceDocumentType, string | null>>
  >({})

  const storedExpirationFor = (type: SubcontractorComplianceDocumentType) =>
    type in uploadedExpirations
      ? uploadedExpirations[type]
      : initialValues?.compliance_documents?.find(
          (document) => document.document_type === type
        )?.expiration_date
  const normalizedInitialValues = useMemo(() => {
    // `defaultName` is a name a caller creating inline (say from a finance row)
    // already typed.
    if (!initialValues)
      return { send_invitation: defaultSendInvitation, first_name: defaultName }
    return {
      ...initialValues,
      w9_validity_date: initialValues.w9_validity_date
        ? dayjs(initialValues.w9_validity_date)
        : undefined,
      cslb_issue_date: initialValues.cslb_issue_date
        ? dayjs(initialValues.cslb_issue_date)
        : undefined,
      // The API returns the existing W-9 as a URL string, not the fileList
      // shape the Upload's `valuePropName="fileList"` expects — handing it
      // one crashes antd's Upload ("(fileList || []).forEach is not a
      // function"). Editing always starts the field empty; the existing
      // file is not lost, just not prefilled — a new upload replaces it.
      w9_document: undefined
    }
  }, [initialValues, defaultSendInvitation, defaultName])

  const onFinish = (values: SubcontractorFormValues) =>
    onSave(toSubcontractorFormData(values, initialValues))

  useEffect(() => {
    dispatch(fetchSubcontractorsFilterOptions())
  }, [dispatch])

  const isCreate = !initialValues

  const overviewSection = (
    <>
      <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Legal Business Name"
                name="legal_business_name"
              >
                <TextInput
                  placeholder="Enter legal business name"
                  size="large"
                />
              </Form.Item>
            </Col>
      </Row>
        {/* Name Fields */}
        <Row gutter={12}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter subcontractor first name"
                }
              ]}
            >
              <TextInput placeholder="Enter first name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="Middle Name"
              name="middle_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor middle name"
                }
              ]}
            >
              <TextInput placeholder="Enter middle name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor last name"
                }
              ]}
            >
              <TextInput placeholder="Enter last name" size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Fields */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" }
              ]}
              normalize={normalizeEmail}
            >
              <TextInput
                placeholder={placeholders.email}
                size="large"
                disableAutoCapitalize
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Phone Number"
              name="phone"
              // Mandatory when adding; an existing subcontractor without one
              // can still be edited without being forced to supply it.
              rules={[{ required: !initialValues, validator: validatePhone }]}
            >
              <PhoneInput placeholder={placeholders.phone} size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Price, Locale & Specialization */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Price per hour"
              name="price_per_hour"
              rules={[
                { required: false, message: "Please enter your price" },
                {
                  type: "number",
                  min: 0,
                  message: "Value must be positive"
                }
              ]}
            >
              <InputNumber
                size="large"
                prefix="$"
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Location"
              name="location"
              rules={[
                {
                  required: false,
                  message: "Please select location"
                }
              ]}
            >
              <SelectComponent
                size="large"
                mode={"multiple"}
                maxTagCount={"responsive"}
                placeholder="Select location"
                options={filterOptions?.["location"] || []}
                optionFilterType="form"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input?.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item<SubcontractorFormValues>
          label="Specialization"
          name="specialization"
          rules={[
            {
              required: false,
              message: "Please select subcontractor specialization"
            }
          ]}
        >
          <SelectComponent
            size="large"
            placeholder="Select custom specialization"
            options={filterOptions?.["specialization"] || []}
            optionFilterType="form"
            mode="multiple"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item hidden={!showToggle}>
          <Space>
            <Form.Item<SubcontractorFormValues>
              noStyle
              name="send_invitation"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Text>Send invitation</Text>
            <Tooltip title="If disabled, the subcontractor will be added without invitation.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Name"
                name="primary_contact_name"
              >
                <TextInput placeholder="Enter contact name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Email"
                name="primary_contact_email"
                rules={[
                  { type: "email", message: "Please enter a valid email" }
                ]}
                normalize={normalizeEmail}
              >
                <TextInput
                  placeholder="Enter contact email"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Phone"
                name="primary_contact_phone"
                rules={[{ validator: validatePhone }]}
              >
                <PhoneInput placeholder="Enter contact phone" size="large" />
              </Form.Item>
            </Col>
          </Row>
    </>
  )

  const documentsSection = (
    <>
            <>
              <Title level={5} className={"!mb-2 !mt-2"}>
                Compliance Documents
              </Title>
              <Text type="secondary" className={"mb-3 block"}>
                Each document is uploaded on its own, so a new file replaces the
                one on file straight away rather than waiting on Save.
              </Text>
              <Flex vertical gap={8} className={"mb-3"}>
                {TRACKED_COMPLIANCE_DOCUMENT_TYPES.map((type) => {
                  const expiration = storedExpirationFor(type)
                  const onFile = expiration !== undefined
                  return (
                    <Flex
                      key={type}
                      justify={"space-between"}
                      align={"center"}
                      gap={8}
                      wrap
                    >
                      <Text>{COMPLIANCE_DOCUMENT_LABELS[type]}</Text>
                      <Flex gap={8} align={"center"} wrap>
                        <Text type={"secondary"}>
                          {onFile
                            ? (expiration ?? "No expiry date")
                            : "Not on file"}
                        </Text>
                        <Button onClick={() => setManagingType(type)}>
                          {onFile ? "Replace" : "Upload"}
                        </Button>
                      </Flex>
                    </Flex>
                  )
                })}
              </Flex>
            </>
        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            CSLB License
          </Title>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="License Number"
                name="cslb_license_number"
                rules={[
                  {
                    pattern: CSLB_LICENSE_NUMBER_PATTERN,
                    message:
                      "Enter a valid CSLB license number (up to 8 digits)"
                  }
                ]}
              >
                <TextInput
                  placeholder="Up to 8 digits"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
            {initialValues && (
              <Col xs={24} md={12}>
                <Form.Item label="Expiration Date">
                  <Flex gap={8} align={"center"}>
                    <TextInput
                      disableAutoCapitalize
                      size="large"
                      disabled
                      className={"flex-1"}
                      value={
                        licenseExpirationOverride === undefined
                          ? (initialValues.cslb_expiration_date ??
                            "No License document on file")
                          : (licenseExpirationOverride ?? "No expiry date")
                      }
                    />
                    <Button onClick={() => setManagingType("license")}>
                      {t("subcontractorDetail.compliance.manageLicense")}
                    </Button>
                  </Flex>
                </Form.Item>
              </Col>
            )}
          </Row>

          {initialValues?.id && managingType && (
            <ComplianceDocumentUploadModal
              open
              subcontractorId={initialValues.id}
              documentType={managingType}
              currentExpirationDate={
                // The License row is the one whose date is also shown above, as
                // the CSLB expiry, so it reads its own override first.
                managingType === "license" && licenseExpirationOverride !== undefined
                  ? licenseExpirationOverride
                  : (storedExpirationFor(managingType) ??
                    (managingType === "license"
                      ? initialValues.cslb_expiration_date
                      : null))
              }
              onClose={() => setManagingType(null)}
              onUploaded={(document) => {
                setUploadedExpirations((current) => ({
                  ...current,
                  [document.document_type]: document.expiration_date
                }))
                if (document.document_type === "license") {
                  setLicenseExpirationOverride(document.expiration_date)
                }
                setManagingType(null)
              }}
            />
          )}

          <Form.Item<SubcontractorFormValues>
            label="Classification"
            name="cslb_classification"
          >
            <SelectComponent
              size="large"
              mode={"multiple"}
              maxTagCount={"responsive"}
              placeholder="Select CSLB classification"
              options={filterOptions?.["specialization"] || []}
              optionFilterType="form"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Status"
                name="cslb_status"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select license status"
                  options={CSLB_STATUS_OPTIONS}
                  optionFilterType="form"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Issue Date"
                name="cslb_issue_date"
              >
                <CustomDatePicker
                  className={"w-full"}
                  size="large"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Contractor Bond Amount"
                name="cslb_bond_amount"
                rules={[{ validator: validateBondAmount }]}
              >
                <InputNumber
                  size="large"
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="25000"
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Bond Number"
                name="cslb_bond_number"
              >
                <TextInput
                  placeholder="Enter bond number"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>

          <Text type="secondary" className={"mb-2 block"}>
            The CSLB also requires a separate bond for the qualifying individual
            when they own under 10% of the company.
          </Text>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Qualifying Individual Bond Amount"
                name="cslb_qualifier_bond_amount"
                rules={[{ validator: validateBondAmount }]}
              >
                <InputNumber
                  size="large"
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="25000"
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Qualifying Individual Bond Number"
                name="cslb_qualifier_bond_number"
              >
                <TextInput
                  placeholder="Enter bond number"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>
        </>
    </>
  )

  const taxPaymentSection = (
    <>
        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            Identity & Tax
          </Title>
          <Text type="secondary" className={"mb-3 block"}>
            Optional — fill in what you have.
          </Text>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Entity Type"
                name="entity_type"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select entity type"
                  options={ENTITY_TYPE_OPTIONS}
                  optionFilterType="form"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Business Address"
                name="business_address"
              >
                <TextInput placeholder="Street address" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="City"
                name="business_city"
              >
                <TextInput placeholder="City" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="State"
                name="business_state"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select state"
                  options={LOCAL_STATES}
                  optionFilterType="form"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="ZIP Code"
                name="business_zip_code"
              >
                <TextInput
                  placeholder="ZIP code"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>

          {entityType === "sole_prop" ? (
            <Form.Item<SubcontractorFormValues>
              label="SSN"
              name="ssn"
              rules={[
                {
                  pattern: SSN_PATTERN,
                  message: "Enter a valid SSN (XXX-XX-XXXX)"
                }
              ]}
            >
              <TextInput
                type="password"
                autoComplete="off"
                disableAutoCapitalize
                placeholder={
                  initialValues?.ssn_last4
                    ? `On file, ending ${initialValues.ssn_last4}`
                    : "XXX-XX-XXXX"
                }
                size="large"
              />
            </Form.Item>
          ) : (
            <Form.Item<SubcontractorFormValues>
              label="EIN"
              name="ein"
              rules={[
                {
                  pattern: EIN_PATTERN,
                  message: "Enter a valid EIN (XX-XXXXXXX)"
                }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                placeholder={
                  initialValues?.ein_last4
                    ? `On file, ending ${initialValues.ein_last4}`
                    : "XX-XXXXXXX"
                }
                size="large"
              />
            </Form.Item>
          )}

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="W-9 Document"
                name="w9_document"
                valuePropName="fileList"
                getValueFromEvent={normFile}
              >
                <DocumentUpload
                  buttonLabel={initialValues ? "Replace file" : "Select file"}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="W-9 Validity Date"
                name="w9_validity_date"
              >
                <CustomDatePicker
                  className={"w-full"}
                  size="large"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

        </>
        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            Payment
          </Title>
          <Form.Item<SubcontractorFormValues>
            label="Payment Method"
            name="payment_method"
          >
            <SelectComponent
              size="large"
              placeholder="Select payment method"
              options={PAYMENT_METHOD_OPTIONS}
              optionFilterType="form"
              allowClear
            />
          </Form.Item>

          {paymentMethod === "ach" && (
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="ACH Routing Number"
                  name="ach_routing_number"
                  rules={[
                    {
                      pattern: ACH_ROUTING_NUMBER_PATTERN,
                      message: "Enter a valid 9-digit routing number"
                    }
                  ]}
                >
                  <TextInput
                    disableAutoCapitalize
                    placeholder={
                      initialValues?.ach_routing_number_last4
                        ? `On file, ending ${initialValues.ach_routing_number_last4}`
                        : "123456789"
                    }
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="ACH Account Number"
                  name="ach_account_number"
                  rules={[
                    {
                      pattern: ACH_ACCOUNT_NUMBER_PATTERN,
                      message: "Enter a valid account number"
                    }
                  ]}
                >
                  <TextInput
                    type="password"
                    autoComplete="off"
                    disableAutoCapitalize
                    placeholder={
                      initialValues?.ach_account_number_last4
                        ? `On file, ending ${initialValues.ach_account_number_last4}`
                        : "Enter account number"
                    }
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </>
    </>
  )

  // One field list per section drives both the tab's error badge and the jump
  // to whichever section holds the first invalid field, reusing the helpers
  // ProjectForm's tabbed form already uses so the two cannot disagree.
  const sections: TabDef[] = [
    {
      key: "overview",
      label: "Overview",
      fields: OVERVIEW_FIELDS,
      render: () => overviewSection
    },
    {
      key: "documents",
      label: "Documents & Compliance",
      fields: DOCUMENT_FIELDS,
      render: () => documentsSection
    },
    {
      key: "tax",
      label: "Tax & Payment",
      fields: TAX_PAYMENT_FIELDS,
      render: () => taxPaymentSection
    }
  ]

  const errorCounts = getTabErrorCounts(sections, form.getFieldsError())

  // A failed save used to leave the user on whichever section they were
  // reading, with the offending field possibly on another one and off screen.
  const handleFinishFailed = ({
    errorFields
  }: ValidateErrorEntity<SubcontractorFormValues>) => {
    const key = findFirstErrorTabKey(sections, errorFields)
    if (key && key !== activeSection) setActiveSection(key)
    const first = errorFields[0]?.name
    if (first) form.scrollToField(first, { behavior: "smooth", block: "center" })
  }


  return (
    <Form<SubcontractorFormValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={handleFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      className="with-default-error"
      initialValues={normalizedInitialValues}
    >
      <Flex
        vertical
        className="pb-4 overflow-auto overflow-x-hidden tailwind-scroll max-h-[75vh]"
      >
        {isCreate ? (
          // Creation asks only what it takes for the record to exist. Every
          // other section is reachable the moment it is saved — on the profile
          // page, and through this same form in edit mode.
          overviewSection
        ) : (
          <Tabs
            activeKey={activeSection}
            onChange={setActiveSection}
            items={sections.map((section) => ({
              key: section.key,
              forceRender: true,
              label: (
                <Flex align="center" gap={6}>
                  {section.label}
                  {(errorCounts.get(section.key) ?? 0) > 0 && (
                    <span className="error-count">
                      {errorCounts.get(section.key)}
                    </span>
                  )}
                </Flex>
              ),
              children: section.render()
            }))}
          />
        )}
      </Flex>

      {/* Form Actions */}
      <Form.Item noStyle>
        <Flex align="center" justify="flex-end" gap="middle">
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={fetching || loading}
            className="min-w-44"
          >
            {initialValues
              ? "Save Changes"
              : sendInvitation
                ? "Send Invitation"
                : "Add Subcontractor"}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}
